import { createRouter, createWebHistory } from 'vue-router';
import { jwtDecode } from 'jwt-decode';

// Fallback JWT decoder in case jwt-decode fails
function jwtDecodeFallback(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(payload);
  } catch (e) {
    return null;
  }
}

// Import your page components
import Login from '@/pages/Login.vue';
import Dashboard from '@/pages/Dashboard.vue';
import Models from '@/pages/Models.vue';
import Sales from '@/pages/Sales.vue';
import Reports from '@/pages/Reports.vue';
import Adjustments from '@/pages/Adjustments.vue';
import Hours from '@/pages/Hours.vue';
import Calendar from '@/pages/Calendar.vue';
import TasksPage from '@/pages/TasksPage.vue';
import Payments from '@/pages/Payments.vue';
import Configuration from '@/pages/Configuration.vue';
import Inventory from '@/pages/Inventory.vue';
import CashBox from '@/pages/CashBox.vue';
import CardExpenses from '@/pages/CardExpenses.vue';
import ModelSchedule from '@/pages/ModelSchedule.vue';
import WeeklySales from '@/pages/WeeklySales.vue';
import AdminSchedule from '@/pages/AdminSchedule.vue';
import Periods from '@/pages/Periods.vue';
import AccountsAccess from '@/pages/AccountsAccess.vue';

const MASTER_ACCOUNT_EMAILS = new Set([
  'carolina.r@mgrouplcs.com',
  'jono@mgrouplcs.com',
  'jorge.n@mgrouplcs.com',
]);

// Define your routes
const routes = [
  { path: '/', redirect: '/login' },
  { path: '/login', name: 'Login', component: Login },
  { 
    path: '/dashboard', 
    name: 'Dashboard', 
    component: Dashboard, 
    meta: { requiresAuth: true },
  },
  {
    path: '/models',
    name: 'Models',
    component: Models,
    meta: { requiresAuth: true },
  },
  {
    path: '/sales',
    name: 'Sales',
    component: Sales,
    meta: { requiresAuth: true },
  }, 
  {
    path: '/reports',
    name: 'Reports',
    component: Reports,
    meta: { requiresAuth: true },
  }, 
  {
    path: '/adjustments',
    name: 'Adjustments',
    component: Adjustments,
    meta: { requiresAuth: true },
  }, 
  {
    path: '/hours',
    name: 'Hours',
    component: Hours,
    meta: { requiresAuth: true },
  },
  {
    path: '/calendar',
    name: 'Calendar',
    component: Calendar,
    meta: { requiresAuth: true },
  },
  {
    path: '/payments',
    name: 'Payments',
    component: Payments,
    meta: { requiresAuth: true },
  },
  {
    path: '/configuration',
    name: 'Configuration',
    component: Configuration,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/accounts-access',
    name: 'AccountsAccess',
    component: AccountsAccess,
    meta: { requiresAuth: true, requiresMasterAccess: true },
  },
  {
    path: '/inventory',
    name: 'Inventory',
    component: Inventory,
    meta: { requiresAuth: true },
  },
  {
    path: '/card-expenses',
    name: 'CardExpenses',
    component: CardExpenses,
    meta: { requiresAuth: true },
  },
  {
    path: '/cash-box',
    name: 'CashBox',
    component: CashBox,
    meta: { requiresAuth: true },
  },
  {
    path: '/model-schedule',
    name: 'ModelSchedule',
    component: ModelSchedule,
    meta: { requiresAuth: true },
  },
  {
    path: '/tasks',
    name: 'Tasks',
    component: TasksPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/weekly-sales',
    name: 'WeeklySales',
    component: WeeklySales,
    meta: { requiresAuth: true },
  },
  {
    path: '/admin-schedule',
    name: 'AdminSchedule',
    component: AdminSchedule,
    meta: { requiresAuth: true, adminScheduleAccess: true },
  },
  {
    path: '/periods',
    name: 'Periods',
    component: Periods,
    meta: { requiresAuth: true },
  },
  {
    path: '/model/:id',
    component: () => import('@/pages/modelProfiles/Index.vue'),
    children: [
      { path: '', redirect: 'overview' },
      { path: 'overview', component: () => import('@/pages/modelProfiles/Overview.vue') },
      { path: 'tasks', component: () => import('@/pages/modelProfiles/Tasks.vue') },
      { path: 'schedule', component: () => import('@/pages/modelProfiles/Schedule.vue') },
      { path: 'development', component: () => import('@/pages/modelProfiles/Development.vue') },
      { path: 'training', component: () => import('@/pages/modelProfiles/Training.vue') },
      { path: 'financials', component: () => import('@/pages/modelProfiles/Financials.vue') }
    ],
    meta: { requiresAuth: true }
  }
];

// Create the router instance
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Navigation Guard for authentication
router.beforeEach((to, from, next) => {
  // Retrieve authToken from localStorage
  const token = localStorage.getItem('authToken');

  let isAuthenticated = false;
  let decoded = null;

  if (token) {
    // Remove potential extra quotes
    let tokenToDecode = token;
    if (typeof token === 'string' && token.length > 1 && token.startsWith('"') && token.endsWith('"')) {
      tokenToDecode = token.substring(1, token.length - 1);
    } else {
      tokenToDecode = token;
    }

    try {
      try {
        decoded = jwtDecode(tokenToDecode);
      } catch (decodeError) {
        decoded = jwtDecodeFallback(tokenToDecode);
      }

      if (!decoded) {
        throw new Error('Failed to decode token using both jwt-decode and fallback');
      }

      const currentTime = Math.floor(Date.now() / 1000);

      if (decoded.exp && decoded.exp > currentTime) {
        isAuthenticated = true;
      } else {
        localStorage.removeItem('authToken'); // Clear the expired token
        isAuthenticated = false;
      }
    } catch (error) {
      localStorage.removeItem('authToken'); // Clear the invalid token
      isAuthenticated = false;
    }
  }

  const requiresAuth = !!to.meta?.requiresAuth;
  const isHr = decoded?.role === 'hr';
  const isAccountant = decoded?.role === 'accountant';
  const isSupport = decoded?.role === 'support';
  const isModel = decoded?.role === 'model';
  const modelHome = isModel && Number(decoded?.model_id) > 0
    ? `/model/${Number(decoded.model_id)}/overview`
    : '/login';
  const isOwnModelProfile = isModel
    && Number(to.params?.id) === Number(decoded?.model_id)
    && to.path.startsWith(`/model/${Number(decoded.model_id)}/`);
  const accountingAllowedRoutes = new Set(['/card-expenses', '/cash-box', '/periods', '/calendar']);
  const hrAllowedRoutes = new Set(['/model-schedule', '/admin-schedule', '/calendar']);
  const supportAllowedRoutes = new Set(['/model-schedule', '/admin-schedule', '/periods', '/calendar', '/models', '/reports', '/adjustments']);
  const isSupportModelProfile = isSupport && to.path.startsWith('/model/');
  const canAccessAdminSchedule = decoded
    && (decoded.role === 'admin'
      || decoded.isAdmin === true
      || decoded.role === 'satellite_monitor'
      || decoded.role === 'hr'
      || decoded.role === 'support'
      || ['carolina.r@mgrouplcs.com', 'jono@mgrouplcs.com', 'jorge.n@mgrouplcs.com'].includes(String(decoded.email || '').trim().toLowerCase()));
  const canAccessConfiguration = decoded
    && (decoded.role === 'admin' || decoded.isAdmin === true);
  const canAccessAccountsAccess = decoded
    && decoded.role === 'admin'
    && MASTER_ACCOUNT_EMAILS.has(String(decoded.email || '').trim().toLowerCase());

  if (requiresAuth && !isAuthenticated) {
    next('/login');
  } else if (isAuthenticated && isModel && to.path === '/login') {
    next(modelHome);
  } else if (isAuthenticated && isModel && !isOwnModelProfile) {
    next(modelHome);
  } else if (isAuthenticated && isHr && !hrAllowedRoutes.has(to.path) && to.path !== '/login') {
    next('/admin-schedule');
  } else if (isAuthenticated && isAccountant && !accountingAllowedRoutes.has(to.path) && to.path !== '/login') {
    next('/card-expenses');
  } else if (isAuthenticated && isSupport && !supportAllowedRoutes.has(to.path) && !isSupportModelProfile && to.path !== '/login') {
    next('/model-schedule');
  } else if (to.meta?.adminScheduleAccess && !canAccessAdminSchedule) {
    next('/dashboard');
  } else if (to.meta?.requiresAdmin && !canAccessConfiguration) {
    next(isHr ? '/admin-schedule' : isAccountant ? '/card-expenses' : isSupport ? '/model-schedule' : '/dashboard');
  } else if (to.meta?.requiresMasterAccess && !canAccessAccountsAccess) {
    next('/dashboard');
  } else if (to.path === '/login' && isAuthenticated) {
    next(isHr ? '/admin-schedule' : isAccountant ? '/card-expenses' : isSupport ? '/model-schedule' : '/dashboard');
  } else {
    next();
  }
});

export default router;
